import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiAddPaymentMethod } from '../../services/api';

import './SecurityQuestions.css';
import {
  getAppError,
  getUserMessageOrValidationError,
} from '../../utils/messages';
import {
  PaymentResponse,
  PaymentResponseCard,
  PaymentResponseCheck,
} from '../../services/types/paymenttypes';
import { IonContent } from '@ionic/react';
import AppPageHeader from '../../components/AppPageHeader';
import PaymentAddExternal from '../payments/PaymentAddExternal';
import PaymentCardHeader from '../payments/PaymentCardHeader';
import PaymentAddForm from '../payments/PaymentAddForm';
import AppLoading from '../../components/AppLoading';
import useEnhancedAuth from '../../EnhancedAuth';
import {
  defaultConfigure,
  EMPTY_FIELD_REASON,
  useCollectJS,
} from '../../hooks/usecollectJS';
import { useToaster } from '../../hooks/useToaster';

type PaymentAddProcessProps = {
  callback: () => void;
  isPageActive: boolean;
};

export default function PaymentAddProcess({
  callback,
  isPageActive,
}: PaymentAddProcessProps) {
  const [t] = useTranslation('global');
  const [present] = useToaster();
  const auth = useEnhancedAuth();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [paymentToken, setPaymentToken] = useState<string>('');
  const [block, setBlock] = useState<string>('external');

  const [name, setName] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [postalcode, setPostalcode] = useState<string>('');
  const [firstname, setFirstname] = useState<string>('');
  const [lastname, setLastname] = useState<string>('');
  const [country, setCountry] = useState<string>('United States');

  const [validFields, setValidFields] = useState<{
    ccnumber: boolean | null;
    ccexp: boolean | null;
    cvv: boolean | null;
  }>({ ccnumber: null, ccexp: null, cvv: null });

  const [paymentResponse, setPaymentResponse] =
    useState<PaymentResponse | null>(null);

  const configure = useMemo(
    () => ({
      ...defaultConfigure,
      callback: (e: {
        card: PaymentResponseCard;
        check: PaymentResponseCheck;
        token: string;
      }) => {
        setPaymentToken(e.token);
        setPaymentResponse(e);
        setBlock('addconfirm');
        setIsLoading(false);
      },
      fieldsAvailableCallback: () => {
        setIsLoading(false);
      },
      validationCallback: (
        changedField: string,
        isValid: boolean,
        reason: string,
      ) => {
        setValidFields((prev) => ({
          ...prev,
          [changedField]: reason === EMPTY_FIELD_REASON ? null : isValid,
        }));
      },
    }),
    [],
  );

  const collectJs = useCollectJS(configure);

  useEffect(() => {
    setIsLoading(true);
  }, []);

  const addCard = () => {
    if (isSubmitting) {
      return;
    }
    setIsLoading(true);
    setIsSubmitting(true);
    apiAddPaymentMethod(
      {
        nickname: name,
        paymentToken: paymentToken,
        cardNumber: paymentResponse?.card.number || '',
        cardType: paymentResponse?.card.type || '',
        expiryDate: paymentResponse?.card.exp || '',
        address: {
          firstName: firstname,
          lastName: lastname,
          street: address,
          street_optional: null,
          city: city,
          state: state,
          stateIsEnum: true,
          country: country,
          zip: postalcode,
        },
      },
      auth,
    )
      .then((response) => {
        setPaymentToken('');
        setPaymentResponse(null);
        setName('');
        callback();
      })
      .catch((error) => {
        console.log('Error occurred while retrieving data', error);
        const apiError = getUserMessageOrValidationError(error);
        if (typeof apiError === 'string') {
          present(getAppError(apiError));
        } else {
          present(getAppError(t('payments.noaddsuccess')));
        }
        setBlock('addconfirm');
      })
      .finally(() => {
        setIsSubmitting(false);
        setIsLoading(false);
      });
  };

  return (
    <>
      {block === 'external' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => callback()}
            title={t('payments.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <AppLoading hidden={!isLoading} />
            <PaymentAddExternal
              paymentToken={paymentToken}
              collectJs={collectJs}
              validFields={validFields}
              setIsLoading={setIsLoading}
            />
          </IonContent>
        </>
      )}
      {block === 'addconfirm' && paymentResponse && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => callback()}
            title={t('payments.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <AppLoading hidden={!isLoading} />
            <PaymentCardHeader
              title={paymentResponse.card.type}
              subTitle={paymentResponse.card.number}
            />
            <PaymentAddForm
              name={name}
              addCard={addCard}
              setName={setName}
              address={address}
              setAddress={setAddress}
              city={city}
              setCity={setCity}
              state={state}
              setState={setState}
              postalcode={postalcode}
              setPostalcode={setPostalcode}
              country={country}
              setCountry={setCountry}
              firstname={firstname}
              setFirstname={setFirstname}
              lastname={lastname}
              setLastname={setLastname}
            ></PaymentAddForm>
          </IonContent>
        </>
      )}
    </>
  );
}
